/**
 * Run a JavaScript regular expression over a text: every match with its position,
 * numbered and named groups (and their positions with the `d` flag), plus an optional
 * replacement.
 *
 * `runRegexJob` is deliberately self-contained — no imports, no module constants, no
 * helpers outside its own body — because the regex tester runs it inside a Web Worker
 * built from its source text (`regexWorkerSource`). A pattern that backtracks
 * catastrophically then freezes only the worker, which the page terminates.
 */

export interface RegexJob {
  pattern: string;
  flags: string;
  text: string;
  /** Null when no replacement preview is wanted. */
  replacement: string | null;
  /** Stop collecting after this many matches. */
  maxMatches: number;
}

export interface RegexMatch {
  index: number;
  end: number;
  text: string;
  /** Numbered groups 1…n; null for a group that did not take part in the match. */
  groups: (string | null)[];
  /** Named groups in pattern order. */
  named: [string, string | null][];
  /** Group positions, only with the `d` flag; index 0 is the whole match. */
  spans: ([number, number] | null)[] | null;
}

export type RegexOutcome =
  | {
      ok: true;
      matches: RegexMatch[];
      /** More matches exist than `maxMatches`. */
      truncated: boolean;
      replaced: string | null;
    }
  | { ok: false; error: string };

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: one self-contained body is the point — it is stringified into a Web Worker, so it cannot call helpers defined outside it.
export function runRegexJob(job: RegexJob): RegexOutcome {
  let regex: RegExp;
  try {
    regex = new RegExp(job.pattern, job.flags);
  } catch (error) {
    return {
      ok: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
  const unicode = regex.unicode || job.flags.includes("v");
  // Helpers live inside so the whole function can be stringified into the worker.
  const groupsOf = (match: RegExpExecArray): (string | null)[] =>
    Array.from(match)
      .slice(1)
      .map((value) => (value === undefined ? null : value));
  const namedOf = (match: RegExpExecArray): [string, string | null][] =>
    Object.entries(match.groups ?? {}).map(([name, value]) => [
      name,
      value === undefined ? null : value,
    ]);
  const spansOf = (
    match: RegExpExecArray
  ): ([number, number] | null)[] | null => {
    const indices = (
      match as RegExpExecArray & {
        indices?: ([number, number] | undefined)[];
      }
    ).indices;
    return indices
      ? Array.from(indices).map((span) => (span ? [span[0], span[1]] : null))
      : null;
  };
  const describe = (match: RegExpExecArray): RegexMatch => ({
    index: match.index,
    end: match.index + match[0].length,
    text: match[0],
    groups: groupsOf(match),
    named: namedOf(match),
    spans: spansOf(match),
  });
  const matches: RegexMatch[] = [];
  let truncated = false;
  if (regex.global) {
    regex.lastIndex = 0;
    let match = regex.exec(job.text);
    while (match !== null) {
      if (matches.length >= job.maxMatches) {
        truncated = true;
        break;
      }
      matches.push(describe(match));
      if (match[0] === "") {
        // An empty match would match again at the same place forever: step past it,
        // by a whole code point in Unicode mode.
        const code = job.text.codePointAt(regex.lastIndex);
        regex.lastIndex +=
          unicode && code !== undefined && code > 0xff_ff ? 2 : 1;
      }
      match = regex.exec(job.text);
    }
  } else {
    const match = regex.exec(job.text);
    if (match !== null) {
      matches.push(describe(match));
    }
  }
  const replaced =
    job.replacement === null
      ? null
      : job.text.replace(new RegExp(job.pattern, job.flags), job.replacement);
  return { ok: true, matches, truncated, replaced };
}

/**
 * A Web Worker script that answers `{ id, job }` messages with `{ id, outcome }`, built
 * from `runRegexJob`'s own source so the worker and the tests run the same code.
 */
export function regexWorkerSource(): string {
  return `"use strict";
const runRegexJob = ${runRegexJob.toString()};
self.onmessage = function (event) {
  const data = event.data;
  self.postMessage({ id: data.id, outcome: runRegexJob(data.job) });
};
`;
}
